import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Handshake, ExternalLink, Users, Building2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Partners = () => {
  const { language } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const texts = {
    en: {
      partnerBadge: 'Strategic Partnership',
      partnerTitle: 'ESOT & EvomatQ',
      partnerSubtitle: 'Joint Venture',
      partnerDescription: 'ESOT-EU and EvomatQ have joined forces to deliver cutting-edge smart storage and automation solutions. Together, we combine engineering excellence with innovative warehouse technology.',
      evomatqDesc: 'Smart Storage Systems & Warehouse Automation',
      visitSite: 'Visit EvomatQ',
      techPartnerBadge: 'Technology Partner',
      techPartnerTitle: 'Tara Robot',
      techPartnerDesc: 'Robotics & Automation Solutions',
      clientsBadge: 'Trusted By Industry Leaders',
      clientsTitle: 'Our Clients',
      clientsSubtitle: 'We are proud to work with leading companies across various industries',
    },
    bg: {
      partnerBadge: 'Стратегическо партньорство',
      partnerTitle: 'ESOT & EvomatQ',
      partnerSubtitle: 'Съвместно предприятие',
      partnerDescription: 'ESOT-EU и EvomatQ обединиха сили, за да предоставят най-съвременни решения за интелигентно съхранение и автоматизация. Заедно съчетаваме инженерно съвършенство с иновативна складова технология.',
      evomatqDesc: 'Интелигентни системи за съхранение и автоматизация на складове',
      visitSite: 'Посетете EvomatQ',
      techPartnerBadge: 'Технологичен партньор',
      techPartnerTitle: 'Tara Robot',
      techPartnerDesc: 'Роботика и автоматизация',
      clientsBadge: 'Доверени от лидери в индустрията',
      clientsTitle: 'Нашите клиенти',
      clientsSubtitle: 'Гордеем се да работим с водещи компании от различни индустрии',
    }
  };

  const t = texts[language] || texts.en;

  const clients = [
    { 
      name: 'Deva Broadcast', 
      logo: 'https://customer-assets.emergentagent.com/job_esot-engineering/artifacts/vi9pb40d_devabroadcast_cover.jpg',
    },
    { 
      name: 'Milara', 
      logo: 'https://customer-assets.emergentagent.com/job_esot-engineering/artifacts/ztryt1i0_milarainternational_Logo_color.png',
    },
    { 
      name: 'Sevic', 
      logo: 'https://customer-assets.emergentagent.com/job_esot-engineering/artifacts/2jwzvly8_68134c5297c94f49b8036a0d_studioPUNKT-Sevic-Branding-Thumbnail-Archive.webp',
    },
    { 
      name: 'Haycad Academy', 
      logo: 'https://customer-assets.emergentagent.com/job_esot-engineering/artifacts/vbmgplpk_images.png',
      rounded: true,
    },
    { 
      name: 'Simobotics', 
      logo: 'https://customer-assets.emergentagent.com/job_esot-engineering/artifacts/nn22dpjr_Simobotics_Logo_Horizontal.png',
    },
  ];

  return (
    <section
      id="partners"
      ref={ref}
      className="relative py-24 lg:py-32 overflow-hidden"
      data-testid="partners-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-[#0F172A]"></div>
      <div className="absolute inset-0 blueprint-grid opacity-20"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* ============ ESOT & EvomatQ Partnership - Hero Style ============ */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="relative mb-24"
        >
          {/* Partnership Badge */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center space-x-2 bg-[#6B8CAE]/10 border border-[#6B8CAE]/30 px-4 py-2"
            >
              <Handshake size={18} className="text-[#6B8CAE]" />
              <span className="text-[#6B8CAE] text-sm font-mono uppercase tracking-wider">
                {t.partnerBadge}
              </span>
            </motion.div>
          </div>

          {/* Main Partnership Card */}
          <div className="relative bg-gradient-to-br from-[#1E293B] to-[#0F172A] border border-[#6B8CAE]/30 p-8 lg:p-12 overflow-hidden">
            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#6B8CAE]/5 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#22D3EE]/5 rounded-full blur-3xl"></div>
            
            {/* Corner Decorations */}
            <div className="absolute top-4 left-4 w-8 h-8 border-l-2 border-t-2 border-[#6B8CAE]"></div>
            <div className="absolute top-4 right-4 w-8 h-8 border-r-2 border-t-2 border-[#6B8CAE]"></div>
            <div className="absolute bottom-4 left-4 w-8 h-8 border-l-2 border-b-2 border-[#6B8CAE]"></div>
            <div className="absolute bottom-4 right-4 w-8 h-8 border-r-2 border-b-2 border-[#6B8CAE]"></div>

            <div className="relative grid lg:grid-cols-2 gap-12 items-center">
              {/* Logos Side */}
              <div className="flex flex-col items-center lg:items-start">
                <div className="flex items-center justify-center space-x-6 mb-8">
                  {/* ESOT Logo */}
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    className="bg-white/5 backdrop-blur-sm border border-white/10 p-4"
                  >
                    <img 
                      src="https://customer-assets.emergentagent.com/job_esot-engineering/artifacts/1onyyd2p_ESOT%20Logo_.jpg" 
                      alt="ESOT-EU" 
                      className="h-16 w-auto object-contain"
                    />
                  </motion.div>
                  
                  {/* Connection Symbol */}
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-0.5 bg-gradient-to-r from-[#6B8CAE] to-[#22D3EE]"></div>
                    <span className="text-[#22D3EE] text-2xl font-bold my-2">&</span>
                    <div className="w-12 h-0.5 bg-gradient-to-r from-[#22D3EE] to-[#6B8CAE]"></div>
                  </div>

                  {/* EvomatQ Logo */}
                  <motion.a
                    href="https://www.evomatq.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05 }}
                    className="bg-white border border-white/10 p-4"
                  >
                    <img 
                      src="https://customer-assets.emergentagent.com/job_esot-engineering/artifacts/kw4p5ctu_Ads%C4%B1z%20tasar%C4%B1m%20%287%29.png" 
                      alt="EvomatQ" 
                      className="h-16 w-auto object-contain"
                    />
                  </motion.a>
                </div>

                <h2 className="text-3xl lg:text-4xl font-bold text-white text-center lg:text-left mb-2">
                  {t.partnerTitle}
                </h2>
                <p className="text-[#22D3EE] font-mono text-sm uppercase tracking-wider">
                  {t.partnerSubtitle}
                </p>
              </div>

              {/* Description Side */}
              <div>
                <p className="text-[#94A3B8] text-lg leading-relaxed mb-6">
                  {t.partnerDescription}
                </p>
                <p className="text-white font-medium mb-6">
                  <Building2 size={18} className="inline mr-2 text-[#6B8CAE]" />
                  {t.evomatqDesc}
                </p>
                <motion.a
                  href="https://www.evomatq.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="btn-primary inline-flex items-center space-x-2"
                  data-testid="evomatq-link"
                >
                  <span>{t.visitSite}</span>
                  <ExternalLink size={16} />
                </motion.a>
              </div>
            </div>
          </div>
        </motion.div>

        {/* ============ Technology Partner - Tara Robot ============ */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-24"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center space-x-2 mb-4">
              <div className="h-px w-12 bg-[#6B8CAE]"></div>
              <span className="text-[#6B8CAE] text-sm font-mono uppercase tracking-wider">
                {t.techPartnerBadge}
              </span>
              <div className="h-px w-12 bg-[#6B8CAE]"></div>
            </div>
          </div>

          <div className="flex justify-center">
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="card-technical p-8 max-w-md text-center"
            >
              <div className="bg-white/5 border border-white/10 p-4 inline-block mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_esot-engineering/artifacts/du4dy8es_find_2018116_122217203.jpg" 
                  alt="Tara Robot" 
                  className="h-20 w-auto object-contain"
                />
              </div>
              <h3 className="text-white text-xl font-semibold mb-2">{t.techPartnerTitle}</h3>
              <p className="text-[#94A3B8]">{t.techPartnerDesc}</p>
            </motion.div>
          </div>
        </motion.div>

        {/* ============ Clients Section ============ */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 mb-4">
              <div className="h-px w-12 bg-[#6B8CAE]"></div>
              <Users size={18} className="text-[#6B8CAE]" />
              <span className="text-[#6B8CAE] text-sm font-mono uppercase tracking-wider">
                {t.clientsBadge}
              </span>
              <div className="h-px w-12 bg-[#6B8CAE]"></div>
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              {t.clientsTitle}
            </h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">
              {t.clientsSubtitle}
            </p>
          </div>

          {/* Clients Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {clients.map((client, index) => (
              <motion.div
                key={client.name}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.4 + index * 0.1 }}
                whileHover={{ scale: 1.05, borderColor: '#6B8CAE' }}
                className="bg-[#1E293B]/50 border border-[#6B8CAE]/20 p-6 flex flex-col items-center justify-center min-h-[140px] transition-colors"
                data-testid={`client-${client.name.toLowerCase().replace(' ', '-')}`}
              >
                {client.logo ? (
                  <div className={`mb-2 flex items-center justify-center ${client.rounded ? 'bg-white/90 rounded-lg p-2' : ''}`}>
                    <img 
                      src={client.logo} 
                      alt={client.name} 
                      className="h-10 w-auto object-contain"
                    />
                  </div>
                ) : (
                  <div className="text-white font-semibold text-lg mb-2">{client.name}</div>
                )}
                <span className="text-[#94A3B8] text-xs font-mono uppercase tracking-wider">
                  {client.name}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default Partners;
